<template>
  <div class="tab-container">
    <app-cells>
      <app-input
        v-model="search_filter"
        class="input--300"
        name="search"
        placeholder="Search"
        type="search"
        @input.native="onLoanSearch"
      />
    </app-cells>
    <app-spinner v-if="!page_loaded"/>
    <template v-else>
      <v-client-table
        :class="{ 'table-default--loading': table_preloader }"
        :columns="table.columns"
        :data="table.items"
        :options="table.options"
        class="table-default"
      >
        <template #print="{ row }">
          <div class="table-default__controls">
            <button
              v-if="!print_preload || print_preload !== `print_${row.id}`"
              type="button"
              :disabled="print_preload"
              @click="onPrint(row)"
            >
              <img src="@/assets/img/print-table-icon.svg" alt="">
            </button>
            <img v-show="print_preload === `print_${row.id}`" src="@/assets/img/preloader-export.svg" alt="">
          </div>
        </template>
        <template #h__employee>
          <div class="table-default__left">Employee</div>
        </template>
        <template #h__position>
          <div class="table-default__left">Position</div>
        </template>
        <template #status="{ row }">
          <span :style="{ backgroundColor: row.status.color }" class="status-tag">
            {{ row.status.name }}
          </span>
        </template>
        <template #created_at="{ row }">{{ formatDateTime(row.created_at) }}</template>
        <template #employee="{ row }">
          <div class="table-default__left">
            <router-link
              :to="{ name: 'employees-profile', params: { id: row.employee.id } }"
              class="table-default__link-border"
            >
              {{ row.employee.full_name }}
            </router-link>
          </div>
        </template>
        <template #position="{ row }">
          <div class="table-default__left">{{ row.employee.position }}</div>
        </template>
        <template #bank_account="{ row }">
          <div class="table-default__left table-default__bank-account-cell">
            <span>{{ bankAccountLabel(row.employee) }}</span>
            <button
              v-if="row.employee.bank_account"
              type="button"
              class="table-default__bank-account-copy-button"
              title="Copy bank account"
              aria-label="Copy bank account"
              @click="copyBankAccount(row.employee.bank_account)"
            ><img src="@/assets/img/copy-account.svg" alt=""></button>
          </div>
        </template>
        <template #amount="{ row }">{{ row.amount.toLocaleString() }} TSh</template>
        <template #employee_notes="{ row }">
          <div class="table-default__controls">
            <div
              v-tooltip="{ content: row.employee_notes, placement: 'right', offset: 10 }"
              class="table-default__activity table-default__activity--notes"
              :class="{'table-default__activity--active': row.employee_notes}"
            ></div>
          </div>
        </template>
        <template #actions="{ row }">
          <div v-if="getActions(row.status.code).length" class="table-default__controls">
            <app-button
              v-for="action in getActions(row.status.code)"
              :key="action.code"
              :theme="action.theme"
              size="small"
              @click.native.stop="runAction(row, action)"
            >
              {{ action.label }}
            </app-button>
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :clickHandler="onClickPagination"
            :container-class="'pagination'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :next-text="'>'"
            :page-class="'pagination__item'"
            :page-count="pagination.pages"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :prev-link-class="'pagination__link pagination__link--prev'"
            :prev-text="'<'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page"/>
      </app-cells>
    </template>
  </div>
</template>

<script>
import { getEmployeesStaffLoans, getEmployeesStaffLoansExportInstallments } from '@/http'
import debounce from 'lodash/debounce'
import { format, parseISO } from 'date-fns'

const actionsByStatus = {
  requested: [
    { code: 'approve', label: 'Approve', theme: 'default' },
    { code: 'reject', label: 'Reject', theme: 'error' },
    { code: 'cancel', label: 'Cancel', theme: 'dark' },
    { code: 'edit', label: 'Edit', theme: 'info' },
  ],
  approved: [
    { code: 'cancel', label: 'Cancel', theme: 'dark' },
    { code: 'disburse', label: 'Disburse', theme: 'info' },
  ],
  disbursed: [
    { code: 'markFullyPaid', label: 'Mark fully paid', theme: 'default' },
    { code: 'abandon', label: 'Abandon', theme: 'error' },
  ],
}

export default {
  name: 'StaffLoansRequests',
  data() {
    return {
      search_filter: '',
      table: {
        items: [],
        columns: [
          'print',
          'status',
          'created_at',
          'employee',
          'position',
          'amount',
          'installments',
          'bank_account',
          'employee_notes',
          'actions',
        ],
        options: {
          headings: {
            print: '',
            status: 'Status',
            created_at: 'Created at',
            employee: 'Employee',
            position: 'Position',
            amount: 'Amount',
            installments: 'Installments',
            bank_account: 'Bank account',
            employee_notes: 'Employee notes',
            actions: 'Actions',
          },
          pagination: {show: false},
          sortable: [],
          perPage: 50,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_preloader: false,
      page_loaded: false,
      print_preload: null,
    }
  },
  created() {
    this.fetchItems()
  },
  methods: {
    fetchItems() {
      this.table_preloader = true
      return getEmployeesStaffLoans({
        page: this.pagination.page,
        page_size: 50,
        search: this.search_filter,
        category: 'requests'
      })
        .then(response => {
          this.table.items = response.data.results
          this.pagination.pages = response.data.pages
          this.pagination.count = response.data.count
          this.table_preloader = false
          this.page_loaded = true
        })
        .catch(() => {
          this.table_preloader = false
        })
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    getActions(status) {
      return actionsByStatus[status] || []
    },
    runAction(row, action) {
      if (action.code === 'edit') {
        this.$emit('edit-loan', row)
        return
      }

      this.$emit('confirm-action', { row, action })
    },
    onPrint(row) {
      const pdfWindow = window.open('', '_blank')
      this.print_preload = `print_${ row.id }`
      getEmployeesStaffLoansExportInstallments(row.id)
        .then(response => {
          const url = response.data && (response.data.url || response.data)
          if (!url) throw new Error('PDF URL is missing')
          if (pdfWindow) pdfWindow.location.replace(url)
          else window.location.href = url
        })
        .catch(() => {
          if (pdfWindow) pdfWindow.close()
          this.$notify({type: 'error', title: 'Unable to open PDF'})
        })
        .finally(() => { this.print_preload = null })
    },
    formatDateTime(date) {
      if (!date) return '-'
      return format(parseISO(date), 'dd MMMM yyyy HH:mm')
    },
    onLoanSearch() {
      this.searchLoan(this)
    },
    searchLoan: debounce(vm => {
      vm.pagination.page = 1
      vm.fetchItems()
    }, 350),
    bankAccountLabel(employee) {
      if (!employee.bank_account) return '-'
      const bank = employee.bank && (employee.bank.short_name || employee.bank.name)
      return bank ? `${bank}: ${employee.bank_account}` : employee.bank_account
    },
    copyBankAccount(account) {
      navigator.clipboard.writeText(account).then(() => {
        this.$notify({type: 'success', title: 'Copied to clipboard'})
      })
    },
  }
}
</script>
